import { type App, effectScope, watch } from 'vue';
import { useTitle } from '@vueuse/core';
import { useClaims } from '@auth/useClaims';
import particulierFavicon from '@assets/icons/particulier-favicon.svg?url';
import '@assets/styles/_scope.css';

const particulierTitleSuffix = ' - Particulier';

function zonderParticulierSuffix(title: string) {
  return title.endsWith(particulierTitleSuffix)
    ? title.slice(0, -particulierTitleSuffix.length)
    : title;
}

function updateParticulierFavicon(isParticulier: boolean) {
  const id = 'particulier-favicon';
  const bestaandFavicon = document.getElementById(id);

  if (isParticulier && !bestaandFavicon) {
    const favicon = document.createElement('link');
    favicon.id = id;
    favicon.rel = 'icon';
    favicon.type = 'image/svg+xml';
    favicon.href = particulierFavicon;
    document.head.append(favicon);
  } else if (!isParticulier) {
    bestaandFavicon?.remove();
  }
}

export interface ScopeBrandingOptions {
  tabBranding?: boolean;
}

/**
 * Documentbrede scope-branding: het kleurthema-attribuut, en (voor documenteigenaren)
 * browsertitel-suffix + favicon. Losstaand van AppLayout zodat ook surfaces zonder die
 * layout (zoals de widgets die in legacy-pagina's mounten) de actieve scope tonen.
 */
export function useScopeBranding(options?: ScopeBrandingOptions) {
  const tabBranding = options?.tabBranding ?? true;
  const claims = useClaims();

  if (tabBranding) {
    const documentTitle = useTitle(null, { observe: true, restoreOnUnmount: false });

    // Routers schrijven na navigatie zelf de browsertitel. Door die wijzigingen te observeren
    // blijft het scope-onderscheid ook staan wanneer de gebruiker naar een andere pagina gaat.
    watch(
      [documentTitle, () => claims.value?.isSuccess, () => claims.value?.isParticulier],
      ([title, isSuccess, isParticulier]) => {
        // Zolang de claims laden is de scope onbekend: dan niets doen
        if (!isSuccess) return;

        const titleZonderScope = zonderParticulierSuffix(title ?? 'SUREnet');
        const titleMetScope = isParticulier
          ? `${titleZonderScope}${particulierTitleSuffix}`
          : titleZonderScope;

        if (title !== titleMetScope) {
          documentTitle.value = titleMetScope;
        }
      },
      { immediate: true },
    );
  }

  // Activeert het scope-kleurenthema (:root[data-scope='particulier'] in _scope.css).
  // Attribuut i.p.v. style.setProperty zodat webmodule-branding (inline styles) blijft winnen.
  watch(
    [() => claims.value?.isSuccess, () => claims.value?.isParticulier],
    ([isSuccess, isParticulier]) => {
      if (!isSuccess) return;

      if (isParticulier) {
        document.documentElement.dataset.scope = 'particulier';
      } else {
        delete document.documentElement.dataset.scope;
      }

      if (tabBranding) {
        updateParticulierFavicon(!!isParticulier);
      }
    },
    { immediate: true },
  );
}

/**
 * Draait de scope-branding buiten componentcontext, zodat hij geen enkele layout of
 * component nodig heeft: effectScope + runWithContext geven de composable de benodigde
 * reactiviteits- en injectiecontext (Vue Query) van de app.
 */
export function installScopeBranding(app: App, options?: ScopeBrandingOptions) {
  const scope = effectScope(true);
  scope.run(() => app.runWithContext(() => useScopeBranding(options)));
  app.onUnmount(() => scope.stop());
}
